<script setup lang="ts">
import Length from "../../../source/js/shared/models/Geometry/Length";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import { endsWithSeparator, limitDecimals, stripNonNumeric, toNumber } from "@/functions/numbers";
import {computed, onBeforeUnmount, onMounted} from "vue";
import { useStore } from "vuex";
import {addSizeToDataLayer} from "@/functions/analytics";
import DrillHoleConfigurationFactory from "@/Models/DrillHoleConfigurations/DrillHoleConfigurationFactory";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();

const drillOption = computed(() => store.getters.drillOption );
const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameter = computed(() => store.getters.minimumDrillHoleDiameter );
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration );
const hasDrillHoleValidatorErrors = computed(() => store.getters.hasDrillHoleValidatorErrors );

const drillHoleDiameter = computed({
	get: () => store.getters.drillHoleDiameter.in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit("updateDrillHoleDiameter", Length.from_cm( limitDecimals( value, 1 ) ).in_mm() );
			store.commit("updateUsingDefaultDrillHoleDimensions", false );
		}
	},
});
const drillHoleHorizontalAmount = computed({
	get: () => store.getters.drillHoleHorizontalAmount,
	set: (value: number|string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit("updateDrillHoleHorizontalAmount", toNumber(stripNonNumeric(value)));
			store.commit("updateUsingDefaultDrillHoleDimensions", false );
		}
	},
});
const drillHoleVerticalAmount = computed({
	get: () => store.getters.drillHoleVerticalAmount,
	set: (value: number|string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit("updateDrillHoleVerticalAmount", toNumber(stripNonNumeric(value)));
			store.commit("updateUsingDefaultDrillHoleDimensions", false );
		}
	},
});
const drillHoleOffset = computed({
	get: () => store.getters.drillHoleOffset.in_cm(),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit("updateDrillHoleOffset", Length.from_cm( limitDecimals( value, 1 ) ).in_mm() );
			store.commit("updateUsingDefaultDrillHoleDimensions", false );
		}
	},
});

document.addEventListener("focusin", focusChanged);

function focusChanged(event): void {
	store.commit("updateFocusedElement", event.target);
}

function drillHoleFieldsWithMessage(): {key: string, message: string}[] {
	const configuration = new DrillHoleConfigurationFactory().get_configuration( 'grid' );

	// filter out keys with valid dimensions
	const invalidKeys = configuration.get_input_keys().filter((field) => !isDrillHoleValid.value(field));

	// get the data we need to render a notice
	return invalidKeys.map((field) => {
		return {
			key: field,
			message: getDrillHoleValidationMessage.value(field),
		};
	});
}

onMounted(async () => {

	// Don't set defaults if we have an old configuration available for radial drill holes.
	if ( lastGeneratedConfiguration.value.drillholes?.type === 'grid' ) {
		return;
	}
});

onBeforeUnmount(() => {
	document.removeEventListener("focusin", focusChanged);
});

</script>

<template>
	<div class="input-container">
		<div class="drill-hole-inputs">
			<dimension-input
				inputRef="drillHoleHorizontalAmount"
				id="drillHoleHorizontalAmount"
				name="drillHoleHorizontalAmount"
				inputmode="numeric"
				:min="1"
				:max="1000"
				:label="$i18n.drillHoleConfiguration.grid.holes_amount_horizontally"
				:invalid="!isDrillHoleValid('drillHoleHorizontalAmount')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.grid.holes_amount_horizontally, drillHoleHorizontalAmount, 'grid-drill-hole')"
				v-model="drillHoleHorizontalAmount"
				:step="1"
			/>

			<dimension-input
				inputRef="drillHoleVerticalAmount"
				id="drillHoleVerticalAmount"
				name="drillHoleVerticalAmount"
				inputmode="numeric"
				:min="1"
				:max="1000"
				:label="$i18n.drillHoleConfiguration.grid.holes_amount_vertically"
				:invalid="!isDrillHoleValid('drillHoleVerticalAmount')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.grid.holes_amount_vertically, drillHoleVerticalAmount, 'grid-drill-hole')"
				v-model="drillHoleVerticalAmount"
				:step="1"
			/>

			<dimension-input
				inputRef="drillHoleDiameter"
				id="drillHoleDiameter"
				name="drillHoleDiameter"
				unit="cm"
				:min="minimumDrillHoleDiameter"
				:label="`${$i18n.drillHoleConfiguration.diameter_drill_hole}`"
				:invalid="!isDrillHoleValid('drillHoleDiameter')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.diameter_drill_hole, drillHoleDiameter, 'grid-drill-hole')"
				v-model="drillHoleDiameter"
			/>

			<dimension-input
				inputRef="drillHoleOffset"
				id="drillHoleOffset"
				name="drillHoleOffset"
				unit="cm"
				:min="minimumDrillHoleDiameter"
				:label="`${$i18n.drillHoleConfiguration.distance_from_edge}`"
				:invalid="!isDrillHoleValid('drillHoleOffset')"
				:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.distance_from_edge, drillHoleOffset, 'grid-drill-hole')"
				v-model="drillHoleOffset"
			/>
		</div>

		<message-container>
			<validation-message
				v-if="drillOption !== 'none'"
				type="notice"
			>
				{{ $i18n.drillHolesNotice }}
			</validation-message>

			<validation-message
				v-for="field in drillHoleFieldsWithMessage()"
				:key="field.key + '-validation-message'"
				:input-id="field.key"
			>
				{{ field.message }}
			</validation-message>
		</message-container>
	</div>
</template>

<style scoped lang="scss">
.input-container {
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;
}

.drill-hole-inputs {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}

</style>
